/**
 * Web Push 구독 관리 (PWA 백그라운드 알림).
 *
 * App.tsx 안의 클로저였던 것을 서비스로 뺐다 — 설정 화면에서도 구독을 켜야 하는데
 * prop 드릴링(App → MobileSettingsScreen → 행 컴포넌트)을 만들지 않기 위해서다.
 * 구독 귀속은 서버가 JWT(req.user)로 판별하므로 클라이언트는 userId를 보내지 않는다.
 */

import { apiFetch } from './apiClient';
import { getClientType } from './authService';
import { isWebNotifyEnabled } from '../utils/webNotifyPref';

/** Base64URL(VAPID 공개키) → Uint8Array. pushManager.subscribe가 요구하는 형식. */
const urlBase64ToUint8Array = (base64String: string): Uint8Array => {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = atob(base64);
  return Uint8Array.from(raw, (c) => c.charCodeAt(0));
};

/**
 * 기존 구독이 지금 서버 키로 만들어진 것인지 바이트 단위로 확인한다.
 *
 * 문자열 비교를 쓰지 않는 이유: 패딩 유무와 알파벳(`+/` vs `-_`)이 환경마다 달라
 * 정규화 실수로 오탐하기 쉬운데, 오탐의 대가가 "실행할 때마다 구독을 갈아엎기"다.
 *
 * ⚠ applicationServerKey를 못 읽는 브라우저(구형 iOS Safari 등)에서는 판정을 포기하고
 *   통과시킨다(fail-open). 판정 불가를 '불일치'로 처리하면 그 기기는 매 실행마다
 *   구독이 갈려 알림이 오히려 끊긴다 — 실패 모드의 무게가 정반대다.
 */
const usesSameServerKey = (existing: ArrayBuffer | null | undefined, expected: Uint8Array): boolean => {
  if (!existing) return true;
  const actual = new Uint8Array(existing);
  if (actual.length !== expected.length) return false;
  for (let i = 0; i < actual.length; i++) {
    if (actual[i] !== expected[i]) return false;
  }
  return true;
};

/** Apple 푸시 서비스(iOS·macOS Safari) 구독인지 — WebKit의 "푸시마다 알림을 띄운다" 규칙이 걸린다. */
const isWebKitEndpoint = (endpoint: string): boolean => {
  try {
    const host = new URL(endpoint).hostname;
    return host === 'push.apple.com' || host.endsWith('.push.apple.com');
  } catch {
    return false;
  }
};

let webKitPushDevice = false;

/**
 * 이 기기가 Apple 웹푸시를 받는가(구독을 마친 뒤에만 true).
 *
 * 참이면 화면이 보이는 동안의 시스템 알림도 서비스워커가 맡는다(App.tsx showPushNotification이 직접 띄우지 않는다).
 * WebKit은 보이는 창이 있어도 푸시마다 알림을 띄우게 강제해서 SW가 건너뛸 수 없고, tag로 합치지도 않으므로
 * 페이지까지 띄우면 같은 메시지가 두 번 뜬다. 구독이 없으면 푸시도 오지 않으므로 false(페이지가 띄운다)가 맞다.
 */
export const isWebKitPushDevice = (): boolean => webKitPushDevice;

/**
 * 지금 제어 중인 서비스워커의 판(SW_PROTOCOL)을 묻는다. 구버전 SW는 답하지 않으므로 1초 뒤 undefined.
 * 서버는 이 값이 2 이상인 구독에만 읽음 정리 푸시를 보낸다(구버전 SW는 그 푸시를 새 메시지로 오해한다).
 */
const askSwProtocol = (reg: ServiceWorkerRegistration): Promise<number | undefined> =>
  new Promise((resolve) => {
    const worker = reg.active;
    if (!worker) {
      resolve(undefined);
      return;
    }
    const channel = new MessageChannel();
    const timer = setTimeout(() => resolve(undefined), 1000);
    channel.port1.onmessage = (event) => {
      clearTimeout(timer);
      const protocol = event.data?.protocol;
      resolve(Number.isInteger(protocol) ? protocol : undefined);
    };
    try {
      worker.postMessage({ type: 'GET_SW_INFO' }, [channel.port2]);
    } catch {
      clearTimeout(timer);
      resolve(undefined);
    }
  });

/** 서버에 구독을 등록한다. 권한이 이미 granted인 상태에서만 호출할 것. */
export const subscribeToPush = async (): Promise<void> => {
  // "이 기기에서 알림 받기"를 끈 상태 — 자동 재구독 경로 전부(로그인 직후·currentUser effect·
  // 권한 배너·설정 패널)가 여길 지나므로 이 한 줄이 유일한 가드다. 호출부 4곳에 개별 가드를
  // 심으면 다음 자동 구독 경로가 추가될 때 반드시 누락된다.
  if (!isWebNotifyEnabled()) return;
  try {
    const reg = await navigator.serviceWorker?.ready;
    if (!reg) return;

    const vapidRes = await fetch('/api/vapid-public-key');
    if (!vapidRes.ok) return;
    const { publicKey } = await vapidRes.json();
    if (!publicKey) return;
    const expectedKey = urlBase64ToUint8Array(publicKey);

    let subscription = await reg.pushManager.getSubscription();

    if (subscription && !usesSameServerKey(subscription.options?.applicationServerKey, expectedKey)) {
      // 서버 VAPID 키가 회전됐다. 이 구독의 endpoint는 영구 403(VapidPkHashMismatch)이라
      // 재전송해 봐야 죽은 행만 늘어난다. 해지하고 새로 만든다.
      console.warn('[WebPush] VAPID key rotated — resubscribing');
      const staleEndpoint = subscription.endpoint;
      try {
        await apiFetch('/web-push/unsubscribe', {
          method: 'POST',
          body: JSON.stringify({ endpoint: staleEndpoint }),
        });
      } catch {
        // 서버 삭제 실패는 무시 — 발송 시 403을 보고 서버가 스스로 정리한다
      }
      try {
        await subscription.unsubscribe();
      } catch {
        // 이미 죽은 구독
      }
      subscription = null;
    }

    if (!subscription) {
      subscription = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: expectedKey as BufferSource,
      });
    }

    webKitPushDevice = isWebKitEndpoint(subscription.endpoint);
    const swProtocol = await askSwProtocol(reg);

    // clientType은 서버가 JWT claim을 우선 쓰고 구 토큰일 때만 이 값을 본다.
    // 'mobile' 구독만 "PC 사용 중" 억제 대상이 되므로 빠지면 폰이 예전처럼 항상 울린다.
    // swProtocol은 답을 받았을 때만 싣는다 — 모르면 서버가 기존 값을 유지한다.
    await apiFetch('/web-push/subscribe', {
      method: 'POST',
      body: JSON.stringify({
        subscription: subscription.toJSON(),
        clientType: getClientType(),
        ...(swProtocol ? { swProtocol } : {}),
      }),
    });
    console.log('[WebPush] Subscribed successfully');
  } catch (err) {
    console.warn('[WebPush] Subscription failed:', err);
  }
};

/**
 * 구독 해제 — 로그아웃 모달에서 "알림 받기" 체크를 해제한 경우에만 호출한다.
 * 기본 정책은 로그아웃 후에도 구독을 유지해 알림을 계속 받는 것(Tauri device_tokens와 동일 철학).
 */
export const unsubscribeFromPush = async (): Promise<void> => {
  // handleLogout이 fire-and-forget으로 부른 직후 localStorage의 authToken을 지우므로
  // 여기서 미리 캡처해 명시 헤더로 넘긴다 (서버 라우트는 JWT 필수)
  const token = localStorage.getItem('authToken');
  try {
    const reg = await navigator.serviceWorker?.ready;
    if (!reg) return;
    const subscription = await reg.pushManager.getSubscription();
    if (!subscription) return;
    try {
      await apiFetch('/web-push/unsubscribe', {
        method: 'POST',
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        body: JSON.stringify({ endpoint: subscription.endpoint }),
      });
    } catch (err) {
      // 서버 삭제 실패 시 남는 고아 row는 다음 발송의 410 응답으로 자동 정리됨
      console.warn('[WebPush] Server unsubscribe failed:', err);
    }
    await subscription.unsubscribe();
    webKitPushDevice = false; // 푸시가 더 오지 않는다 — 화면을 보는 중의 알림은 다시 페이지가 맡는다
    console.log('[WebPush] Unsubscribed on logout');
  } catch (err) {
    console.warn('[WebPush] Unsubscribe failed:', err);
  }
};
